import React from 'react';

/**
 * Material Symbol Icon Component
 * Uses Google Material Symbols for consistent, scalable icons
 *
 * @param {string} name - Material Symbol icon name (e.g., 'add', 'delete', 'search')
 * @param {string} variant - 'outlined' (default), 'rounded', 'sharp'
 * @param {string} size - 'xs', 'sm', 'md' (default), 'lg', 'xl', '2xl'
 * @param {string} weight - 'light', 'regular' (default), 'medium', 'bold'
 * @param {boolean} filled - If true, uses filled style (FILL=1)
 * @param {string} color - 'primary', 'success', 'danger', 'warning', 'info', 'text-primary', 'text-secondary', 'text-tertiary', or any CSS color
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 *
 * @example
 * <Icon name="add" size="md" color="primary" />
 * <Icon name="delete" size="sm" filled color="danger" />
 * <Icon name="search" variant="rounded" color="text-secondary" />
 */
export default function Icon({
  name = 'help',
  variant = 'outlined',
  size = 'md',
  weight = 'regular',
  filled = false,
  color = 'text-primary',
  className = '',
  style = {},
  ...props
}) {
  // Map variant to class name
  const variantClass = `material-symbols-${variant}`;

  // Map size and weight to class names
  const sizeClass = `icon-${size}`;
  const weightClass = `icon-${weight}`;

  // Build color class
  const colorClass = color.startsWith('#') || color.startsWith('rgb') ? '' : `icon-${color}`;

  // Combine all classes
  const classes = [
    variantClass,
    sizeClass,
    weightClass,
    filled && 'icon-filled',
    colorClass,
    className
  ]
    .filter(Boolean)
    .join(' ');

  // Build style object
  const iconStyle = {
    ...style,
    ...(color.startsWith('#') || color.startsWith('rgb') ? { color } : {})
  };

  return (
    <span
      className={classes}
      style={iconStyle}
      {...props}
    >
      {name}
    </span>
  );
}

/**
 * Common Material Symbols Icons
 * Reference for icon names: https://fonts.google.com/icons
 */
export const Icons = {
  // Navigation
  home: 'home',
  menu: 'menu',
  close: 'close',
  arrow_back: 'arrow_back',
  arrow_forward: 'arrow_forward',
  arrow_up: 'arrow_up',
  arrow_down: 'arrow_down',

  // Actions
  add: 'add',
  edit: 'edit',
  delete: 'delete',
  search: 'search',
  filter: 'filter_list',
  sort: 'sort',
  download: 'download',
  upload: 'upload',
  share: 'share',
  copy: 'content_copy',
  print: 'print',

  // User & Account
  person: 'person',
  people: 'group',
  people_outline: 'groups',
  account: 'account_circle',
  settings: 'settings',
  logout: 'logout',
  login: 'login',

  // Status
  check: 'check',
  close_circle: 'close',
  error: 'error',
  warning: 'warning',
  info: 'info',
  help: 'help',
  success: 'check_circle',

  // File & Folder
  file: 'description',
  folder: 'folder',
  folder_open: 'folder_open',
  document: 'document_scanner',
  attachment: 'attach_file',

  // Communication
  email: 'mail',
  phone: 'phone',
  message: 'mail',
  notification: 'notifications',
  chat: 'chat',

  // Calendar & Time
  calendar: 'calendar_today',
  clock: 'schedule',
  time: 'access_time',
  date: 'today',

  // Content
  list: 'list',
  grid: 'grid_view',
  table: 'table_chart',
  card: 'card_view',
  eye: 'visibility',
  eye_off: 'visibility_off',
  lock: 'lock',
  unlock: 'lock_open',

  // Education
  school: 'school',
  class: 'class',
  student: 'person',
  teacher: 'groups',
  assignment: 'assignment',
  quiz: 'quiz',
  book: 'library_books',
  course: 'auto_stories',

  // Finance
  money: 'money',
  payment: 'payment',
  receipt: 'receipt',
  invoice: 'receipt_long',
  card: 'credit_card',
  wallet: 'wallet',

  // Analytics & Reports
  analytics: 'analytics',
  dashboard: 'dashboard',
  report: 'assessment',
  chart: 'bar_chart',
  graph: 'trending_up',
  statistics: 'data_usage',

  // Common
  star: 'star',
  heart: 'favorite',
  flag: 'flag',
  pin: 'location_on',
  link: 'link',
  expand: 'expand_more',
  collapse: 'expand_less',
  more: 'more_vert',
  refresh: 'refresh',
  loading: 'hourglass_empty',
  save: 'save',
  done: 'done',
  undo: 'undo',
  redo: 'redo',
};
